import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useOutletContext, useParams, useSearchParams } from 'react-router-dom'

import { createAbTest, listAbTests, setAbTestStatus } from '../../../api/abTests.js'
import { listElements } from '../../../api/elements.js'
import {
  addElementToGroupSegment,
  addExistingSegmentationToGroupConfig,
  createGroupConfigDynamicRule,
  listGroupConfigElementBindings,
  listGroupConfigDynamicRules,
  listGroupConfigSegments,
  listGroupConfigs,
  listGroupConfigVersions,
  setGroupConfigVersionStatus,
  updateGroupConfigElementBinding,
} from '../../../api/groupConfigs.js'
import { getSegmentation, listSegmentations } from '../../../api/segmentations.js'
import { canTransitionAbTestStatus, getAvailableAbTestStatuses } from '../../../local/abTestStatusStateMachine.js'
import { actions } from '../../../local/remoteConfigStore.js'
import { useRemoteConfigAppState, useRemoteConfigMemo } from '../../../local/useRemoteConfigStore.js'
import {
  buildDynamicRuleElementsPayload,
  createDynamicRuleElement,
  getDynamicRuleElementMode,
  getDynamicRuleTypes,
  getUserParamByKey,
  getUserParamOptions,
  validateDynamicRuleDraft,
} from '../dynamicRules/dynamicRuleFormUtils.js'
import { useFeedback } from '../../../components/ui/FeedbackProvider.jsx'

const DYNAMIC_RULE_TYPES = getDynamicRuleTypes()
const USER_PARAM_OPTIONS = getUserParamOptions()

function formatDate(value) {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleString()
}

function sortByNumber(value) {
  return Number.isFinite(value) ? value : 0
}

function renderDynamicRuleElementInput(mode, element, onElementChange) {
  if (mode === 'ml') {
    return (
      <input
        className="textInput"
        value={element.leftBoundary}
        onChange={onElementChange(element.id, 'leftBoundary')}
        placeholder="ML model name"
      />
    )
  }

  if (mode === 'cyclic') {
    return (
      <div className="builderRuleGridWide">
        <input
          className="textInput"
          type="number"
          value={element.leftBoundary}
          onChange={onElementChange(element.id, 'leftBoundary')}
          placeholder="Order"
        />
        <input
          className="textInput"
          type="number"
          value={element.rightBoundary}
          onChange={onElementChange(element.id, 'rightBoundary')}
          placeholder="Duration"
        />
        <input
          className="textInput"
          value={element.output}
          onChange={onElementChange(element.id, 'output')}
          placeholder="Output"
        />
      </div>
    )
  }

  if (mode === 'select_single') {
    const option = getUserParamByKey('payer_segmentation')
    return (
      <div className="builderRuleGridSplit">
        <select
          className="textInput"
          value={element.leftBoundary}
          onChange={onElementChange(element.id, 'leftBoundary')}
        >
          <option value="">Select condition</option>
          {(option?.values || []).map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
        <textarea
          className="textInput textAreaMin70"
          value={element.output}
          onChange={onElementChange(element.id, 'output')}
          placeholder="Output"
        />
      </div>
    )
  }

  if (mode === 'single') {
    return (
      <div className="builderRuleGridSplit">
        <input
          className="textInput"
          value={element.leftBoundary}
          onChange={onElementChange(element.id, 'leftBoundary')}
          placeholder="Condition"
        />
        <textarea
          className="textInput textAreaMin70"
          value={element.output}
          onChange={onElementChange(element.id, 'output')}
          placeholder="Output"
        />
      </div>
    )
  }

  return (
    <div className="builderRuleGridWide">
      <input
        className="textInput"
        value={element.leftBoundary}
        onChange={onElementChange(element.id, 'leftBoundary')}
        placeholder="From"
      />
      <input
        className="textInput"
        value={element.rightBoundary}
        onChange={onElementChange(element.id, 'rightBoundary')}
        placeholder="To (not included)"
      />
      <textarea
        className="textInput textAreaMin70"
        value={element.output}
        onChange={onElementChange(element.id, 'output')}
        placeholder="Output"
      />
    </div>
  )
}

function chooseFallbackDefaultSegmentation(segmentations) {
  const list = Array.isArray(segmentations) ? [...segmentations] : []
  if (!list.length) return null

  const defaults = list.filter((seg) => seg.isDefault)
  if (defaults.length > 0) {
    defaults.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))
    return defaults[0]
  }

  list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
  return list[0] || null
}

function getDraftVersion(appState, groupConfigId) {
  const normalizedGroupConfigId = String(groupConfigId || '').trim()
  const versions = Object.values(appState.groupConfigVersions || {}).filter(
    (v) =>
      String(v.groupConfigId || '').trim() === normalizedGroupConfigId &&
      String(v.status || '').trim().toLowerCase() === 'draft',
  )
  versions.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))
  return versions[0] || null
}

function buildPreview(appState, groupConfig, versionId) {
  const groupSegments = Object.values(appState.groupSegments || {})
    .filter((seg) => seg.groupConfigVersionId === versionId)
    .sort((a, b) => sortByNumber(a.priority) - sortByNumber(b.priority))

  const segments = groupSegments.map((gs) => {
    const segmentation = appState.segmentations?.[gs.segmentationId] || null
    const bindings = Object.values(appState.elementBindings || {})
      .filter((b) => b.groupSegmentId === gs.id)
      .sort((a, b) => sortByNumber(a.order) - sortByNumber(b.order))
      .map((binding) => {
        const element = appState.elements?.[binding.elementId] || null
        return {
          id: binding.id,
          order: binding.order,
          conditionExpression: binding.conditionExpression,
          element,
        }
      })

    const abTests = Object.values(appState.abTests || {})
      .filter((test) => test.groupConfigId === groupConfig.id && test.segmentKey === gs.segmentKey)
      .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')))
      .map((test) => {
        const variants = Object.values(appState.abVariants || {})
          .filter((v) => v.abTestId === test.id)
          .sort((a, b) => sortByNumber(a.order) - sortByNumber(b.order))
          .map((variant) => {
            const variantBindings = Object.values(appState.abVariantElementBindings || {})
              .filter((b) => b.abVariantId === variant.id)
              .sort((a, b) => sortByNumber(a.order) - sortByNumber(b.order))
              .map((binding) => ({
                id: binding.id,
                order: binding.order,
                conditionExpression: binding.conditionExpression,
                element: appState.elements?.[binding.elementId] || null,
              }))
            return { ...variant, bindings: variantBindings }
          })
        return { ...test, variants }
      })

    return {
      id: gs.id,
      segmentKey: gs.segmentKey,
      priority: gs.priority,
      isEnabled: gs.isEnabled,
      isDefault: gs.isDefault,
      segmentation,
      bindings,
      abTests,
    }
  })

  return {
    groupConfig,
    version: appState.groupConfigVersions?.[versionId] || null,
    segments,
  }
}

export default function GroupConfigBuilderPage() {
  const outlet = useOutletContext()
  const { groupConfigId, id: applicationIdFromParams } = useParams()
  const applicationId = outlet?.applicationId || applicationIdFromParams
  const [searchParams] = useSearchParams()
  const { confirm } = useFeedback()
  const selectedRemoteConfigId = searchParams.get('remoteConfigId') || ''
  const appState = useRemoteConfigAppState(applicationId)
  const storeActions = useRemoteConfigMemo(() => actions(), [])

  const groupConfig = appState?.groupConfigs?.[groupConfigId] || null
  const draft = groupConfig ? getDraftVersion(appState, groupConfigId) : null
  const currentRemoteConfigId = String(selectedRemoteConfigId || draft?.id || '').trim()
  const [bootstrapStatus, setBootstrapStatus] = useState('idle')
  const [bootstrapError, setBootstrapError] = useState(null)

  useEffect(() => {
    let cancelled = false

    async function refreshSegmentations() {
      try {
        const segmentations = await listSegmentations(applicationId, {
          groupConfigId,
        })
        if (cancelled) return
        storeActions.hydrateSegmentations(applicationId, segmentations)
      } catch {
        // Keep builder usable even if segmentations refresh fails.
      }
    }

    refreshSegmentations()

    function handleFocus() {
      refreshSegmentations()
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        refreshSegmentations()
      }
    }

    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      cancelled = true
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [applicationId, groupConfigId, storeActions])

  useEffect(() => {
    let cancelled = false

    const shouldUseExistingDraft =
      groupConfig &&
      draft &&
      (!selectedRemoteConfigId || String(draft.id) === String(selectedRemoteConfigId))

    if (shouldUseExistingDraft) {
      setBootstrapStatus('ready')
      setBootstrapError(null)

      async function refreshBuilderDependencies() {
        try {
          const [segmentations, elements, draftSegments, draftBindings, abTests] = await Promise.all([
            listSegmentations(applicationId, {
              groupConfigId,
            }),
            listElements(applicationId),
            listGroupConfigSegments(applicationId, groupConfigId, {
              remoteConfigId: selectedRemoteConfigId || null,
            }),
            listGroupConfigElementBindings(applicationId, groupConfigId, {
              remoteConfigId: selectedRemoteConfigId || null,
            }),
            listAbTests(applicationId, {
              groupConfigId,
              remoteConfigId: selectedRemoteConfigId || null,
            }),
          ])
          if (cancelled) return
          storeActions.hydrateSegmentations(applicationId, segmentations)
          storeActions.hydrateElements(applicationId, elements)
          storeActions.hydrateGroupSegmentsForDraft(
            applicationId,
            String(groupConfigId),
            draftSegments,
            selectedRemoteConfigId || draft?.id || null,
          )
          storeActions.hydrateElementBindingsForDraft(
            applicationId,
            String(groupConfigId),
            draftBindings,
            selectedRemoteConfigId || draft?.id || null,
          )
          storeActions.hydrateAbTests(applicationId, abTests)
        } catch {
          // Keep builder usable even if background refresh fails.
        }
      }
      refreshBuilderDependencies()

      return () => {
        cancelled = true
      }
    }

    async function bootstrapBuilderState() {
      setBootstrapStatus('loading')
      setBootstrapError(null)
      try {
        // Bootstrap with minimal critical data first, then load heavy dependencies in background.
        const [configs, segmentations] = await Promise.all([
          listGroupConfigs(applicationId),
          listSegmentations(applicationId, {
            groupConfigId,
          }),
        ])
        if (cancelled) return

        storeActions.hydrateSegmentations(applicationId, segmentations)

        const apiGroupConfig = configs.find((item) => String(item.id) === String(groupConfigId))
        if (!apiGroupConfig) {
          throw new Error('Group config not found')
        }

        let defaultSegmentation =
          segmentations.find((item) => {
            if (!apiGroupConfig.defaultSegmentationId) return false
            return String(item.id) === String(apiGroupConfig.defaultSegmentationId)
          }) || null
        if (!defaultSegmentation && apiGroupConfig.defaultSegmentationId) {
          defaultSegmentation = await getSegmentation(
            applicationId,
            apiGroupConfig.defaultSegmentationId,
          )
        }
        if (!defaultSegmentation) {
          defaultSegmentation = chooseFallbackDefaultSegmentation(segmentations)
        }
        if (!defaultSegmentation) {
          throw new Error('Cannot open builder: no segmentations found for this application')
        }
        if (cancelled) return

        const fallbackDraftRemoteConfigId = `local-draft-${apiGroupConfig.id}`
        const fallbackDefaultGroupSegmentId = `local-default-segment-${apiGroupConfig.id}`

        storeActions.hydrateGroupConfigDraft(applicationId, {
          groupConfigId: apiGroupConfig.id,
          name: apiGroupConfig.name,
          description: apiGroupConfig.description,
          startBundleVersion: apiGroupConfig.startBundleVersion,
          draftRemoteConfigId:
            selectedRemoteConfigId || apiGroupConfig.draftRemoteConfigId || fallbackDraftRemoteConfigId,
          defaultSegmentationId: apiGroupConfig.defaultSegmentationId || defaultSegmentation.id,
          defaultGroupSegmentId: apiGroupConfig.defaultGroupSegmentId || fallbackDefaultGroupSegmentId,
          defaultSegmentation,
          createdAt: apiGroupConfig.createdAt,
          updatedAt: apiGroupConfig.updatedAt,
        })

        if (!cancelled) {
          setBootstrapStatus('ready')
          setBootstrapError(null)
        }

        async function refreshBuilderDependencies() {
          try {
            const [elements, draftSegments, draftBindings, abTests] = await Promise.all([
              listElements(applicationId),
              listGroupConfigSegments(applicationId, groupConfigId, {
                remoteConfigId: selectedRemoteConfigId || null,
              }),
              listGroupConfigElementBindings(applicationId, groupConfigId, {
                remoteConfigId: selectedRemoteConfigId || null,
              }),
              listAbTests(applicationId, {
                groupConfigId,
                remoteConfigId: selectedRemoteConfigId || null,
              }),
            ])
            if (cancelled) return
            storeActions.hydrateElements(applicationId, elements)
            storeActions.hydrateGroupSegmentsForDraft(
              applicationId,
              String(apiGroupConfig.id),
              draftSegments,
              selectedRemoteConfigId || apiGroupConfig.draftRemoteConfigId || null,
            )
            storeActions.hydrateElementBindingsForDraft(
              applicationId,
              String(apiGroupConfig.id),
              draftBindings,
              selectedRemoteConfigId || apiGroupConfig.draftRemoteConfigId || null,
            )
            storeActions.hydrateAbTests(applicationId, abTests)
          } catch {
            // Keep builder usable even if background refresh fails.
          }
        }

        refreshBuilderDependencies()
      } catch (error) {
        if (cancelled) return
        setBootstrapStatus('error')
        setBootstrapError(error)
      }
    }

    bootstrapBuilderState()

    return () => {
      cancelled = true
    }
  }, [applicationId, groupConfigId, selectedRemoteConfigId])

  useEffect(() => {
    let cancelled = false
    setDynamicRulesState({
      status: 'loading',
      items: [],
      error: null,
    })

    listGroupConfigDynamicRules(applicationId, groupConfigId)
      .then((items) => {
        if (cancelled) return
        setDynamicRulesState({
          status: 'ok',
          items,
          error: null,
        })
      })
      .catch((loadError) => {
        if (cancelled) return
        setDynamicRulesState({
          status: 'error',
          items: [],
          error: loadError,
        })
      })

    return () => {
      cancelled = true
    }
  }, [applicationId, groupConfigId])

  const segments = useMemo(() => {
    if (!currentRemoteConfigId) return []
  const list = Object.values(appState?.groupSegments || {})
      .filter((seg) => String(seg.groupConfigVersionId || '').trim() === currentRemoteConfigId)
      .sort((a, b) => sortByNumber(a.priority) - sortByNumber(b.priority))
    return list
  }, [appState, currentRemoteConfigId])

  const [selectedSegmentId, setSelectedSegmentId] = useState(null)

  useEffect(() => {
    if (!segments.length) return
    if (segments.some((seg) => seg.id === selectedSegmentId)) return
    const desiredSegmentKey = searchParams.get('segmentKey')
    const byKey = desiredSegmentKey
      ? segments.find((seg) => seg.segmentKey === desiredSegmentKey)
      : null
    const defaultSeg = byKey || segments.find((seg) => seg.isDefault) || segments[0]
    setSelectedSegmentId(defaultSeg?.id || null)
  }, [segments, selectedSegmentId, searchParams])

  const selectedGroupSegment = selectedSegmentId ? appState?.groupSegments?.[selectedSegmentId] : null
  const selectedBindings = useMemo(() => {
    if (!selectedGroupSegment) return []
  const list = Object.values(appState?.elementBindings || {})
      .filter((b) => b.groupSegmentId === selectedGroupSegment.id)
      .sort((a, b) => sortByNumber(a.order) - sortByNumber(b.order))
    return list
  }, [appState, selectedGroupSegment])

  const [addSegmentationId, setAddSegmentationId] = useState('')
  const [bindElementId, setBindElementId] = useState('')
  const [error, setError] = useState(null)
  const [isAddingSegmentation, setIsAddingSegmentation] = useState(false)
  const [isBindingElement, setIsBindingElement] = useState(false)
  const [isAddingAbTest, setIsAddingAbTest] = useState(false)
  const [isCreatingDynamicRule, setIsCreatingDynamicRule] = useState(false)

  const [newAbTest, setNewAbTest] = useState({ name: '', status: 'draft' })
  const [selectedAbTestId, setSelectedAbTestId] = useState('')
  const [newVariantName, setNewVariantName] = useState('')
  const [variantAddElementId, setVariantAddElementId] = useState('')
  const [variantAddCondition, setVariantAddCondition] = useState('')
  const [newDynamicRule, setNewDynamicRule] = useState({
    name: '',
    ruleType: 'Value',
    userParam: USER_PARAM_OPTIONS[0]?.key || '',
    defaultOutput: '',
  })
  const [newDynamicRuleElements, setNewDynamicRuleElements] = useState([createDynamicRuleElement()])
  const [dynamicRulesState, setDynamicRulesState] = useState({
    status: 'loading',
    items: [],
    error: null,
  })

  const availableSegmentations = useMemo(() => {
    const normalizedGroupConfigId = String(groupConfigId || '').trim()
    const all = Object.values(appState?.segmentations || {}).filter(
      (seg) =>
        String(seg?.groupConfigId ?? seg?.groupConfig?.id ?? '').trim() ===
        normalizedGroupConfigId,
    )
    all.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    return all
  }, [appState, groupConfigId])

  const usedSegmentationIds = useMemo(() => {
    if (!currentRemoteConfigId) return new Set()
    const ids = new Set()
    for (const seg of Object.values(appState?.groupSegments || {})) {
      if (String(seg.groupConfigVersionId || '').trim() !== currentRemoteConfigId) continue
      ids.add(String(seg.segmentationId || '').trim())
    }
    return ids
  }, [appState, currentRemoteConfigId])

  const selectableSegmentations = useMemo(() => {
    return availableSegmentations.filter(
      (seg) => !usedSegmentationIds.has(String(seg.id || '').trim()),
    )
  }, [availableSegmentations, usedSegmentationIds])

  const availableElements = useMemo(() => {
    const all = Object.values(appState?.elements || {})
    all.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
    return all
  }, [appState])

  const selectableElements = useMemo(() => {
    return availableElements.filter(
      (el) =>
        el.status === 'published' &&
        String(el.groupConfigId || '') === String(groupConfigId || ''),
    )
  }, [availableElements, groupConfigId])

  const preview = useMemo(() => {
    if (!appState || !groupConfig || !currentRemoteConfigId) return null
    return buildPreview(appState, groupConfig, currentRemoteConfigId)
  }, [appState, groupConfig, currentRemoteConfigId])

  const segmentAbTests = useMemo(() => {
    if (!selectedGroupSegment) return []
    const segmentKey = selectedGroupSegment.segmentKey
    const tests = Object.values(appState?.abTests || {})
      .filter(
        (t) =>
          String(t.groupConfigId || '').trim() === String(groupConfigId || '').trim() &&
          String(t.segmentKey || '').trim() === String(segmentKey || '').trim(),
      )
      .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')))
    return tests
  }, [appState, groupConfigId, selectedGroupSegment])

  const selectedAbTest = selectedAbTestId ? appState?.abTests?.[selectedAbTestId] : null

  const selectedAbVariants = useMemo(() => {
    if (!selectedAbTest) return []
    const variants = Object.values(appState?.abVariants || {})
      .filter((v) => v.abTestId === selectedAbTest.id)
      .sort((a, b) => sortByNumber(a.order) - sortByNumber(b.order))
    return variants
  }, [appState, selectedAbTest])

  const variantElementsByVariantId = useMemo(() => {
    const map = {}
    for (const binding of Object.values(appState?.abVariantElementBindings || {})) {
      if (!map[binding.abVariantId]) map[binding.abVariantId] = []
      map[binding.abVariantId].push(binding)
    }
    for (const variantId of Object.keys(map)) {
      map[variantId].sort((a, b) => sortByNumber(a.order) - sortByNumber(b.order))
    }
    return map
  }, [appState])

  useEffect(() => {
    if (!segmentAbTests.length) {
      setSelectedAbTestId('')
      return
    }
    const desiredTestId = searchParams.get('abTestId')
    if (desiredTestId && segmentAbTests.some((t) => t.id === desiredTestId)) {
      setSelectedAbTestId(desiredTestId)
      return
    }
    if (segmentAbTests.some((t) => t.id === selectedAbTestId)) return
    setSelectedAbTestId(segmentAbTests[0].id)
  }, [segmentAbTests, selectedAbTestId])

  useEffect(() => {
    if (!segmentAbTests.length) {
      setIsAddingAbTest(true)
      return
    }
    setIsAddingAbTest(false)
  }, [segmentAbTests.length, selectedSegmentId])

  async function handleReleaseDraft() {
    setError(null)
    const confirmed = await confirm({
      title: 'Release draft',
      message: 'Release current draft? A new draft will be cloned from the released version.',
      confirmLabel: 'Release',
      tone: 'default',
    })
    if (!confirmed) return
    storeActions.releaseGroupConfigDraft(applicationId, groupConfigId)
  }

  async function handleAddExistingSegmentation() {
    setError(null)
    if (!addSegmentationId || isAddingSegmentation) return
    setIsAddingSegmentation(true)
    try {
      const createdSegment = await addExistingSegmentationToGroupConfig(
        applicationId,
        groupConfigId,
        addSegmentationId,
      )
      storeActions.addSegmentationToDraft(applicationId, groupConfigId, addSegmentationId, {
        groupSegmentId: createdSegment?.id,
        segmentKey: createdSegment?.segmentKey,
        priority: createdSegment?.priority,
        isEnabled: createdSegment?.isEnabled,
        isDefault: createdSegment?.isDefault,
        createdAt: createdSegment?.createdAt,
        updatedAt: createdSegment?.updatedAt,
      })
      setAddSegmentationId('')
    } catch (e) {
      setError(e)
    } finally {
      setIsAddingSegmentation(false)
    }
  }

  function handleBindExistingElement() {
    setError(null)
    if (!selectedGroupSegment || !bindElementId || isBindingElement) return
    setIsBindingElement(true)
    addElementToGroupSegment(
      applicationId,
      groupConfigId,
      selectedGroupSegment.id,
      bindElementId,
      '',
    )
      .then((binding) => {
        storeActions.bindElement(
          applicationId,
          selectedGroupSegment.id,
          bindElementId,
          { conditionExpression: '' },
          {
            bindingId: binding?.id,
            order: binding?.order,
            createdAt: binding?.createdAt,
            updatedAt: binding?.updatedAt,
          },
        )
        setBindElementId('')
      })
      .catch((e) => {
        setError(e)
      })
      .finally(() => {
        setIsBindingElement(false)
      })
  }

  function onNewDynamicRuleChange(field) {
    return (e) => {
      const value = e.target.value
      setNewDynamicRule((prev) => {
        const next = { ...prev, [field]: value }
        if (field === 'ruleType') {
          if (value === 'Cyclic' || value === 'ETL') {
            next.userParam = ''
          } else if (!next.userParam) {
            next.userParam = USER_PARAM_OPTIONS[0]?.key || ''
          }
        }
        return next
      })
    }
  }

  function onNewDynamicRuleElementChange(elementId, field) {
    return (e) => {
      const nextValue = e.target.value
      setNewDynamicRuleElements((prev) =>
        prev.map((item) => (item.id === elementId ? { ...item, [field]: nextValue } : item)),
      )
    }
  }

  function addNewDynamicRuleElement() {
    setNewDynamicRuleElements((prev) => [...prev, createDynamicRuleElement()])
  }

  function removeNewDynamicRuleElement(elementId) {
    setNewDynamicRuleElements((prev) => prev.filter((item) => item.id !== elementId))
  }

  async function handleCreateDynamicRule() {
    setError(null)
    if (isConfigReadOnly) return
    const name = String(newDynamicRule.name || '').trim()
    if (!name) {
      setError(new Error('Dynamic rule name is required'))
      return
    }
    const validationError = validateDynamicRuleDraft({
      ruleType: newDynamicRule.ruleType,
      userParam: newDynamicRule.userParam,
      elements: newDynamicRuleElements,
      defaultOutput: newDynamicRule.defaultOutput,
    })
    if (validationError) {
      setError(new Error(validationError))
      return
    }

    const dynamicRuleElements = buildDynamicRuleElementsPayload({
      ruleType: newDynamicRule.ruleType,
      userParam: newDynamicRule.userParam,
      elements: newDynamicRuleElements,
      defaultOutput: newDynamicRule.defaultOutput,
    })
    if (dynamicRuleElements.length === 0) {
      setError(new Error('Add at least one valid dynamic rule element'))
      return
    }
    if (isCreatingDynamicRule) return

    setIsCreatingDynamicRule(true)
    try {
      const createdRule = await createGroupConfigDynamicRule(applicationId, groupConfigId, {
        name,
        ruleType: newDynamicRule.ruleType,
        userParam: newDynamicRule.userParam,
        dynamicRuleElements,
      })
      if (!createdRule) {
        throw new Error('Dynamic rule was not created')
      }
      setDynamicRulesState((prev) => {
        const nextItems = [createdRule, ...(prev.items || [])]
        return {
          status: 'ok',
          items: nextItems,
          error: null,
        }
      })
      setNewDynamicRule({
        name: '',
        ruleType: 'Value',
        userParam: USER_PARAM_OPTIONS[0]?.key || '',
        defaultOutput: '',
      })
      setNewDynamicRuleElements([createDynamicRuleElement()])
    } catch (createError) {
      const detail = createError?.body?.detail
      if (detail) {
        setError(new Error(Array.isArray(detail) ? detail.join(', ') : String(detail)))
      } else {
        setError(createError)
      }
    } finally {
      setIsCreatingDynamicRule(false)
    }
  }

  async function handleCreateAbTest() {
    setError(null)
    if (isConfigReadOnly) return
    if (!selectedGroupSegment) return
    const name = String(newAbTest.name || '').trim()
    if (!name) {
      setError(new Error('AB test name is required'))
      return
    }
    try {
      const created = await createAbTest(applicationId, {
        groupConfigId,
        groupSegmentId: selectedGroupSegment?.id ?? null,
        name,
        status: newAbTest.status,
      })
      if (!created) throw new Error('AB test was not created')
      storeActions.hydrateAbTests(applicationId, [created])
      setNewAbTest({ name: '', status: 'draft' })
      setSelectedAbTestId(created.id)
      setIsAddingAbTest(false)
    } catch (e) {
      setError(e)
    }
  }

  function handleAddVariant() {
    setError(null)
    if (!selectedAbTest) return
    try {
      storeActions.addAbVariant(applicationId, selectedAbTest.id, newVariantName)
      setNewVariantName('')
    } catch (e) {
      setError(e)
    }
  }

  function handleAddVariantElement(variantId) {
    setError(null)
    if (!variantAddElementId) return
    try {
      storeActions.addAbVariantElement(applicationId, variantId, variantAddElementId, {
        conditionExpression: variantAddCondition,
      })
      setVariantAddElementId('')
      setVariantAddCondition('')
    } catch (e) {
      setError(e)
    }
  }

  const newDynamicRuleMode = getDynamicRuleElementMode(newDynamicRule.ruleType, newDynamicRule.userParam)
  const canRemoveNewDynamicRuleElement = newDynamicRuleElements.length > 1
  const selectedSegmentName = selectedGroupSegment
    ? appState.segmentations?.[selectedGroupSegment.segmentationId]?.name ||
      selectedGroupSegment.segmentKey ||
      'Unnamed segment'
    : ''
  const selectedPrimaryBinding = selectedBindings[0] || null
  const selectedPrimaryElement = selectedPrimaryBinding
    ? appState?.elements?.[selectedPrimaryBinding.elementId] || null
    : null
  const configAbTestsCount = useMemo(() => {
    return Object.values(appState?.abTests || {}).filter(
      (test) => String(test.groupConfigId || '').trim() === String(groupConfigId || '').trim(),
    ).length
  }, [appState, groupConfigId])

  const [bindElementIdBySegmentId, setBindElementIdBySegmentId] = useState({})
  const [bindingSegmentIdInProgress, setBindingSegmentIdInProgress] = useState('')
  const [configVersionState, setConfigVersionState] = useState({
    status: 'loading',
    version: null,
    error: null,
  })
  const [isUpdatingConfigStatus, setIsUpdatingConfigStatus] = useState(false)
  const [showStatusSwitchHint, setShowStatusSwitchHint] = useState(false)
  const [previewExpanded, setPreviewExpanded] = useState(false)
  const statusUpdateInFlightRef = useRef(false)

  const segmentBindingsById = useMemo(() => {
    const map = {}
    for (const segment of segments) {
    const bindings = Object.values(appState?.elementBindings || {})
        .filter((binding) => binding.groupSegmentId === segment.id)
        .sort((a, b) => sortByNumber(a.order) - sortByNumber(b.order))
      map[segment.id] = bindings
    }
    return map
  }, [appState, segments])

  const allSegmentsHaveElement = useMemo(
    () =>
      segments.length === 0 ||
      segments.every((seg) => (segmentBindingsById[seg.id] || []).length > 0),
    [segments, segmentBindingsById],
  )

  const configVersionStatus = String(configVersionState.version?.status || '').trim().toLowerCase()
  const isConfigReadOnly = configVersionStatus === 'released'

  useEffect(() => {
    if (allSegmentsHaveElement) {
      setShowStatusSwitchHint(false)
    }
  }, [allSegmentsHaveElement])

  useEffect(() => {
    let cancelled = false
    setConfigVersionState({
      status: 'loading',
      version: null,
      error: null,
    })

    listGroupConfigVersions(applicationId, groupConfigId)
      .then((versions) => {
        if (cancelled) return
        const currentVersionId = String(selectedRemoteConfigId || draft?.id || '').trim()
        const currentVersion =
          versions.find((version) => String(version.id) === currentVersionId) ||
          versions.find((version) => String(version.id) === String(draft?.id || '').trim()) ||
          null
        setConfigVersionState({
          status: 'ok',
          version: currentVersion,
          error: null,
        })
      })
      .catch((loadError) => {
        if (cancelled) return
        setConfigVersionState({
          status: 'error',
          version: null,
          error: loadError,
        })
      })

    return () => {
      cancelled = true
    }
  }, [applicationId, groupConfigId, selectedRemoteConfigId, draft?.id])

  async function handleSetConfigStatus(nextStatus) {
    const currentVersion = configVersionState.version
    if (!currentVersion?.id || !nextStatus || isUpdatingConfigStatus) return
    if (isConfigReadOnly) return
    if (String(currentVersion.status || '') === String(nextStatus || '')) return
    if (statusUpdateInFlightRef.current) return
    if (!allSegmentsHaveElement) {
      setShowStatusSwitchHint(true)
      return
    }

    statusUpdateInFlightRef.current = true
    setError(null)
    setShowStatusSwitchHint(false)
    setIsUpdatingConfigStatus(true)
    try {
      const updated = await setGroupConfigVersionStatus(
        applicationId,
        groupConfigId,
        currentVersion.id,
        nextStatus,
      )
      setConfigVersionState((prev) => ({
        ...prev,
        status: 'ok',
        version: {
          ...(prev.version || {}),
          status: updated?.status || nextStatus,
          availableStatuses:
            Array.isArray(updated?.availableStatuses) && updated.availableStatuses.length
              ? updated.availableStatuses
              : prev.version?.availableStatuses || [],
        },
      }))
    } catch (e) {
      setError(e)
    } finally {
      statusUpdateInFlightRef.current = false
      setIsUpdatingConfigStatus(false)
    }
  }

  function handleBindElementForSegment(segmentId, options = {}) {
    const existingBindingId = String(options?.existingBindingId || '').trim()
    const existingElementId = String(options?.existingElementId || '').trim()
    const nextElementId = String(bindElementIdBySegmentId[segmentId] || '').trim()
    if (!segmentId || !nextElementId || bindingSegmentIdInProgress) return
    if (existingBindingId && nextElementId === existingElementId) return

    setError(null)
    setBindingSegmentIdInProgress(segmentId)
    if (existingBindingId) {
      updateGroupConfigElementBinding(applicationId, groupConfigId, existingBindingId, {
        elementId: nextElementId,
      })
        .then(() => {
          storeActions.updateBinding(applicationId, existingBindingId, {
            elementId: nextElementId,
          })
        })
        .catch((e) => {
          setError(e)
        })
        .finally(() => {
          setBindingSegmentIdInProgress('')
        })
      return
    }
    addElementToGroupSegment(applicationId, groupConfigId, segmentId, nextElementId, '')
      .then((binding) => {
        storeActions.bindElement(
          applicationId,
          segmentId,
          nextElementId,
          { conditionExpression: '' },
          {
            bindingId: binding?.id,
            order: binding?.order,
            createdAt: binding?.createdAt,
            updatedAt: binding?.updatedAt,
          },
        )
        setBindElementIdBySegmentId((prev) => ({
          ...prev,
          [segmentId]: '',
        }))
      })
      .catch((e) => {
        setError(e)
      })
      .finally(() => {
        setBindingSegmentIdInProgress('')
      })
  }

  if (bootstrapStatus === 'loading' || bootstrapStatus === 'idle') {
    return (
      <section className="card sectionCard">
        <div className="cardTitle">Loading builder...</div>
        <p className="muted inlineMessage">
          Loading group config state from API.
        </p>
      </section>
    )
  }

  if (!groupConfig || !draft) {
    return (
      <section className="card sectionCard">
        <div className="cardTitle">Builder is unavailable</div>
        <p className="muted inlineMessage">
          {bootstrapError
            ? String(bootstrapError?.message || bootstrapError)
            : 'Group config or draft version was not found.'}
        </p>
        <div className="sectionActions">
          <Link to={`/applications/${applicationId}/configs/${groupConfigId}`} className="button buttonGhost">
            Back
          </Link>
        </div>
      </section>
    )
  }

  return (
    <section className="card sectionCard">
      <div className="pageHeader pageHeaderTight">
        <div className="cardTitle">Config</div>
        <div className="buttonGroup">
          <Link to={`/applications/${applicationId}/configs/${groupConfigId}`} className="button buttonGhost">
            Back
          </Link>
        </div>
      </div>

      {error ? <div className="formError">{String(error?.message || error)}</div> : null}

      <div className="cardSectionBottom">
        <div className="builderConfigMetaLine">
          <span>
            Group config: <strong>{groupConfig.name || 'Untitled'}</strong>
          </span>
          <span>
            Config:{' '}
            <strong>
              {configVersionState.version?.bundleVersion ||
                configVersionState.version?.id ||
                draft.id}
            </strong>
          </span>
          <label className="builderStatusControl">
            <span>Status</span>
            <select
              className="textInput"
              value={configVersionState.version?.status || 'Draft'}
              onChange={(e) => handleSetConfigStatus(e.target.value)}
              disabled={
                isUpdatingConfigStatus ||
                configVersionState.status === 'loading' ||
                !configVersionState.version?.id ||
                configVersionStatus === 'released'
              }
            >
              {(
                configVersionState.version?.availableStatuses?.length
                  ? configVersionState.version.availableStatuses
                  : [configVersionState.version?.status || 'Draft']
              ).map((statusValue) => (
                <option key={statusValue} value={statusValue}>
                  {statusValue}
                </option>
              ))}
            </select>
          </label>
          {showStatusSwitchHint ? (
            <p className="muted inlineMessage">
              Select an element for each segment before changing status.
            </p>
          ) : null}
        </div>
      </div>

      <div className="cardSectionBottom">
        <div className="cardTitle">Segments</div>

        {segments.length === 0 ? (
          <p className="muted inlineMessage">
            No segments yet.
          </p>
        ) : (
          <div className="builderTableWrap">
            <table className="table" aria-label="Segments and elements">
              <thead>
                <tr>
                  <th>Segment</th>
                  <th>Element</th>
                  <th className="alignRight">Actions</th>
                </tr>
              </thead>
              <tbody>
                {segments.map((seg) => {
                  const segmentation = appState.segmentations?.[seg.segmentationId]
                  const isAllUsersSegment =
                    Boolean(seg.isDefault) ||
                    String(segmentation?.name || '')
                      .trim()
                      .toLowerCase() === 'all users'
                  const bindings = segmentBindingsById[seg.id] || []
                  const primaryBinding = bindings[0] || null
                  const primaryElement = primaryBinding
                    ? appState.elements?.[primaryBinding.elementId] || null
                    : null
                  const savedElementId = String(primaryBinding?.elementId || '').trim()
                  const currentBindSelectValue = String(
                    bindElementIdBySegmentId[seg.id] ?? savedElementId,
                  ).trim()

                  return (
                    <tr key={seg.id}>
                      <td>
                        <div className="textStrong">
                          {seg.priority}. {segmentation?.name || 'Unnamed segment'}
                        </div>
                        <div className="muted mutedXs">
                          {seg.isDefault ? 'Default' : 'Non-default'}
                          {!seg.isEnabled ? ' • Disabled' : ''}
                        </div>
                      </td>
                      <td>
                        <div className="builderInlineRow">
                          <select
                            className="textInput"
                            value={currentBindSelectValue}
                            onChange={(e) =>
                              setBindElementIdBySegmentId((prev) => ({
                                ...prev,
                                [seg.id]: e.target.value,
                              }))
                            }
                            disabled={isConfigReadOnly}
                          >
                            <option value="">Select element…</option>
                            {selectableElements.map((el) => (
                              <option key={el.id} value={el.id}>
                                {el.name || el.id} ({el.elementType}, v{el.version}, {el.status})
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="button buttonPrimary"
                            onClick={() =>
                              handleBindElementForSegment(seg.id, {
                                existingBindingId: primaryBinding?.id,
                                existingElementId: primaryBinding?.elementId,
                              })
                            }
                            disabled={isConfigReadOnly}
                          >
                            {bindingSegmentIdInProgress === seg.id ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      </td>
                      <td className="alignRight">
                        <span className="tableActions">
                          <button
                            type="button"
                            className="button buttonGhost builderMiniButton"
                            onClick={() => storeActions.moveGroupSegment(applicationId, seg.id, 'up')}
                            disabled={isConfigReadOnly || isAllUsersSegment || seg.priority === 1}
                          >
                            Up
                          </button>
                          <button
                            type="button"
                            className="button buttonGhost builderMiniButton"
                            onClick={() => storeActions.moveGroupSegment(applicationId, seg.id, 'down')}
                            disabled={isConfigReadOnly || isAllUsersSegment || seg.priority === segments.length}
                          >
                            Down
                          </button>
                          <button
                            type="button"
                            className="button buttonGhost builderMiniButton"
                            onClick={async () => {
                              const confirmed = await confirm({
                                title: 'Remove segment',
                                message: 'Remove this segment from the group?',
                                confirmLabel: 'Remove',
                                tone: 'danger',
                              })
                              if (!confirmed) return
                              storeActions.removeGroupSegment(applicationId, seg.id)
                            }}
                            disabled={isConfigReadOnly || isAllUsersSegment || segments.length <= 1}
                          >
                            Remove
                          </button>
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="builderAddRow">
          <div className="builderInlineRow">
            <select
              className="textInput"
              value={addSegmentationId}
              onChange={(e) => setAddSegmentationId(e.target.value)}
              disabled={isConfigReadOnly}
            >
              <option value="">Select segmentation…</option>
              {selectableSegmentations.map((seg) => (
                <option key={seg.id} value={seg.id}>
                  {seg.name || seg.id}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="button buttonPrimary"
              onClick={handleAddExistingSegmentation}
              disabled={isConfigReadOnly || !addSegmentationId || isAddingSegmentation}
            >
              {isAddingSegmentation ? 'Adding…' : 'Add'}
            </button>
          </div>
          <div className="muted mutedSm">
            Manage segmentations in <Link to={`/applications/${applicationId}/segments`}>Segments</Link>.
          </div>
        </div>
      </div>

      <div className="builderPreviewFullWidth" style={{ marginTop: '1rem' }}>
        <button
          type="button"
          className="button buttonGhost"
          onClick={() => setPreviewExpanded((v) => !v)}
        >
          {previewExpanded ? '▼' : '▶'} Preview
        </button>
        {previewExpanded && (
          <div style={{ marginTop: '0.5rem' }}>
            {!preview ? (
              <p className="muted inlineMessage">
                Preview unavailable.
              </p>
            ) : (
              <pre className="codeBlock inlineMessage">
                {JSON.stringify(preview, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

